import type { Big } from 'big.js';
import styled, { type DefaultTheme } from 'styled-components';
import { MEDIA, getLoadingAnimation, maxWidth, parseColor, setAlpha } from '../../styles';
import { format } from '../../utils';
import { BlotterTableWrapper } from '../BlotterTable';
import { Box, Flex, HStack, VStack } from '../Core';
import { DropdownMenu } from '../Filters/DropdownFilter/styles';
import { ToggleButtonFilterWrapper } from '../Filters/ToggleButtonFilter/styles';
import { Text } from '../Text';

export const placeholder = <Text color="colors.gray.080">&mdash;&mdash;</Text>;
export const Placeholder = styled(Box)`
  border-radius: ${({ theme }) => theme.borderRadiusDefault}px;
  ${({ theme }) =>
    getLoadingAnimation(theme, true, 'backgroundCard', setAlpha(0.08, parseColor(theme, 'backgroundCard') ?? ''))}
  height: 16px;
  width: 32px;
`;

export const SparklineWrapper = styled(Box)`
  line {
    transition: stroke-opacity 200ms ease;
  }
  text {
    transition: opacity 200ms ease;
    fill: ${({ theme }) => theme.colorTextSubtle};
    opacity: 0;
  }

  &:hover {
    line,
    text {
      opacity: 1;
    }
  }
`;
SparklineWrapper.defaultProps = {
  h: '16px',
  w: '100%',
};

export const FavoriteCell = styled(Flex)``;
FavoriteCell.defaultProps = {
  pl: '1px',
};

export const CurrencyIconCell = styled(Flex)`
  color: ${({ theme }) => theme.colorTextImportant};
`;

export const SymbolCell = styled(HStack).attrs({
  justifyContent: 'flex-start',
})`
  cursor: pointer;

  &,
  & > ${VStack} {
    overflow: hidden;
  }
  & > ${VStack} > ${Text} {
    text-overflow: ellipsis;
    overflow: hidden;
    word-break: break-word;
    width: 100%;
  }
`;

export const PriceCell = styled(Flex).attrs({
  justifyContent: 'flex-end',
  gap: 'spacing4',
})`
  cursor: pointer;
`;
PriceCell.defaultProps = {
  alignItems: 'center',
};

export const VolumeCell = styled(Flex).attrs({
  justifyContent: 'flex-end',
})``;

export const ChangeCell = styled(Flex)``;
ChangeCell.defaultProps = {
  justifyContent: 'flex-end',
  w: '100%',
  pl: '4px',
};

export const WatchListFiltersWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  align-items: center;
  gap: ${({ theme }) => theme.spacing4}px;
  position: relative;

  ${maxWidth(MEDIA.small)} {
    margin: 0;
  }

  ${DropdownMenu} {
    width: 120px;
  }
`;

export const WatchListWrapper = styled.div.attrs<{ showOnlyFavorites: boolean }>(({ showOnlyFavorites }) => ({
  className: showOnlyFavorites ? 'show-only-favorites' : undefined,
}))<{ showOnlyFavorites: boolean }>`
  display: flex;
  height: 100%;
  flex-direction: column;
  background: ${({ theme }) => theme.backgroundContent};
  padding-top: ${({ theme }) => theme.spacing12}px;

  ${ToggleButtonFilterWrapper} {
    display: contents;
  }

  .ag-cell {
    &.slim-cell {
      padding: 0;
      border-width: 0;
    }
  }

  ${BlotterTableWrapper} {
    .ag-column-first {
      padding-left: ${({ theme }) => theme.spacing16}px;
    }
    .ag-column-last {
      padding-right: ${({ theme }) => theme.spacing16}px;
    }
  }

  &:not(.show-only-favorites) {
    .drag-handle {
      display: none;
    }
  }

  .ag-header-cell {
    .ag-header-cell-comp-wrapper {
      overflow: visible;
    }
    &.bid-header {
      color: ${({ theme }) => theme.colorTextPositive};
      justify-content: center;

      .ag-header-cell-comp-wrapper {
        width: auto;
        overflow: visible;
      }
    }
    &.offer-header {
      color: ${({ theme }) => theme.colorTextNegative};
      justify-content: center;

      .ag-header-cell-comp-wrapper {
        width: auto;
        overflow: visible;
      }
    }
    &.slim-header {
      padding: 0;
      overflow: visible;
      justify-content: center;

      .ag-header-cell-comp-wrapper {
        width: auto;
        overflow: visible;
      }
    }
  }

  ${BlotterTableWrapper}.ag-theme-balham-dark .ag-pinned-right-cols-container {
    box-shadow: none;
  }
`;

export const SearchWrapper = styled(Box)`
  transition: 200ms right ease;
  overflow: hidden;
  flex: 0 0 auto;
`;

export const PercentChange = styled(({ value, theme }: { value: Big; theme: DefaultTheme }) => {
  return (
    <Text
      color={parseColor(theme, value.gt(0) ? 'green.lighten' : value.lt(0) ? 'red.lighten' : 'gray.080')}
      weight="fontWeightMedium"
      style={{ fontSize: '11px', lineHeight: '12px' }}
    >
      {value.gt(0) ? '+' : ''}
      {format(value, { spec: '1.00', round: true })}%
    </Text>
  );
})`
  position: absolute;
`;
